import React, { useMemo, useState, useEffect } from 'react';

/*
  TablaLecturas V2 (mejorada)
  - Mantiene filtros dependientes (dist, nivel, instit, rol) y lógica de avgMatchesRangeAnyMonth
  - Añade filtros: provincia (prov), grado (grade) y sección (section)
  - Prop `prov`: si se envía, sólo muestra data de esa provincia (y el filtro prov se inicia con ese valor)
  - Cabecera tipo "Top Lectores 2025"
  - Botón "Mostrar todos / Ocultar todos" detalles
  - Fila resaltada cuando Detalles está activo para ese estudiante
  - Ordenar por promedio (avg) asc/desc

  Uso:
    <TablaLecturas
      data={miJson}
      pageSize={50}
      prov="SIHUAS" // opcional
    />
*/

const MONTH_LABELS = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

export default function TablaLecturas({ data = {}, pageSize = 50, prov }) {
  // normalizar data: convertir input object a array de usuarios
  const usuariosBase = useMemo(() => {
    let arr = Object.entries(data || {}).map(([id, v]) => ({ id, ...v }));
    if (prov) {
      const p = String(prov).toUpperCase();
      arr = arr.filter(u => String(u.prov || '').toUpperCase() === p);
    }
    return arr;
  }, [data, prov]);

  // estados de UI
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [page, setPage] = useState(1);
  const [sortAvg, setSortAvg] = useState('none'); // 'none' | 'asc' | 'desc'

  // filtros
  const [fProv, setFProv] = useState(prov || '');
  const [fDist, setFDist] = useState('');
  const [fName, setFName] = useState('');
  const [fNivel, setFNivel] = useState('');
  const [fInstit, setFInstit] = useState('');
  const [fRol, setFRol] = useState('');
  const [fAvg, setFAvg] = useState('');
  const [fGrade, setFGrade] = useState('');
  const [fSection, setFSection] = useState('');
  const [allExpanded, setAllExpanded] = useState(false);

  useEffect(() => {
    setLoading(false);
  }, []);

  // helper: calcular rango de meses (estático 2025 hasta dic) según prov
  function startForProv(localProv) {
    if (!localProv) return { y: 2025, m: 1 };
    const p = String(localProv).toUpperCase();
    if (p === 'SIHUAS') return { y: 2025, m: 1 };
    if (p === 'ASUNCION') return { y: 2025, m: 10 };
    return { y: 2025, m: 1 };
  }

  function monthsRange(startYear, startMonthInclusive) {
    const res = [];
    const endYear = 2025, endMonth = 12;
    let y = startYear, m = startMonthInclusive;
    while (y < endYear || (y === endYear && m <= endMonth)) {
      const key = `${y}_${String(m).padStart(2, '0')}`;
      const label = `${MONTH_LABELS[m - 1]} ${y}`;
      res.push({ key, label, month: m, year: y });
      m++;
      if (m > 12) { m = 1; y++; }
    }
    return res;
  }

  // color mapping (por si lo necesitas en otros sitios)
  function mrToColor(mr) {
    if (mr == null) return '#9CA3AF'; // gray-400
    const pct = Number(mr) * 100;
    if (Number.isNaN(pct)) return '#9CA3AF';
    if (pct < 45) return '#DC2626';
    if (pct < 65) return '#F97316';
    if (pct < 80) return '#F59E0B';
    if (pct < 90) return '#3B82F6';
    return '#16A34A';
  }

  // mapping grade → etiqueta amigable
  function gradeLabel(grade) {
    if (grade == null) return 'Usuario GENÉRICO';
    const g = Number(grade);
    if (!Number.isFinite(g)) return 'Usuario GENÉRICO';

    if (g >= 0 && g <= 2) {
      const map = {
        0: 'INICIAL 3 años',
        1: 'INICIAL 4 años',
        2: 'INICIAL 5 años',
      };
      return map[g] || 'INICIAL';
    }
    if (g >= 3 && g <= 8) {
      const grado = g - 2;
      return `PRIMARIA ${grado}°`;
    }
    if (g >= 9 && g <= 13) {
      const grado = g - 8;
      return `SECUNDARIA ${grado}°`;
    }
    if (g >= 20 && g <= 22) return 'Docente INICIAL';
    if (g >= 23 && g <= 28) return 'Docente PRIMARIA';
    if (g >= 29 && g <= 33) return 'Docente SECUNDARIA';

    return 'Usuario GENÉRICO';
  }

  // % helpers para las esferas
  function pctNumber(mr) {
    if (mr == null) return null;
    return Math.max(0, Math.min(100, Math.round(Number(mr) * 100)));
  }

  function mrColor(mr) {
    if (mr == null) return '#9CA3AF';
    const pct = Number(mr) * 100;
    if (pct < 45) return '#DC2626';
    if (pct < 65) return '#F97316';
    if (pct < 80) return '#F59E0B';
    if (pct < 90) return '#3B82F6';
    return '#16A34A';
  }

  function avgMatchesRangeAnyMonth(logros = {}, rangeKey) {
    if (!rangeKey) return true;

    const mrs = Object.values(logros)
      .map(x => {
        const v = Number(x && x.mr);
        return Number.isFinite(v) ? v * 100 : NaN;
      })
      .filter(v => !Number.isNaN(v));

    if (mrs.length === 0) return false;

    const eps = 1e-9;
    const allBelow = t => mrs.every(m => m < t - eps);
    const someAtLeast = t => mrs.some(m => m >= t - eps);

    switch (rangeKey) {
      case '0-45':
        return allBelow(45);
      case '46-65':
        return someAtLeast(45) && allBelow(65);
      case '66-80':
        return someAtLeast(65) && allBelow(80);
      case '81-90':
        return someAtLeast(80) && allBelow(90);
      case '91-100':
        return someAtLeast(90);
      default:
        return true;
    }
  }

  // --- opciones dependientes con counts ---
  const opcionesD = useMemo(() => {
    const campos = ['prov', 'dist', 'nivel', 'instit', 'rol', 'grade', 'section'];
    const res = {};

    campos.forEach((campo) => {
      const lista = usuariosBase.filter((u) => {
        if (campo !== 'prov' && fProv && String(u.prov) !== fProv) return false;
        if (campo !== 'dist' && fDist && String(u.dist) !== fDist) return false;

        if (fName) {
          const full = `${u.name || ''} ${u.family || ''}`.toLowerCase();
          if (!full.includes(fName.toLowerCase())) return false;
        }

        if (campo !== 'nivel' && fNivel && String(u.nivel) !== fNivel) return false;
        if (campo !== 'instit' && fInstit && String(u.instit) !== fInstit) return false;
        if (campo !== 'rol' && fRol && String(u.rol) !== fRol) return false;

        if (campo !== 'grade' && fGrade && gradeLabel(u.grade) !== fGrade) return false;
        if (campo !== 'section' && fSection && String(u.section || '') !== fSection) return false;

        if (fAvg && !avgMatchesRangeAnyMonth(u.logros, fAvg)) return false;
        return true;
      });

      const counts = {};
      lista.forEach((u) => {
        let v = u[campo];
        if (campo === 'grade') v = gradeLabel(u.grade);
        if (v != null) {
          const key = String(v);
          counts[key] = (counts[key] || 0) + 1;
        }
      });

      const options = Object.keys(counts).map(k => ({ val: k, count: counts[k] }));
      options.sort((a, b) => b.count - a.count || a.val.localeCompare(b.val));
      res[campo] = options;
    });

    return res;
  }, [usuariosBase, fProv, fDist, fName, fNivel, fInstit, fRol, fAvg, fGrade, fSection]);

  // aplicar TODOS los filtros para filas mostradas
  const usuariosFiltrados = useMemo(() => {
    const list = usuariosBase.filter((u) => {
      if (fProv && String(u.prov || '') !== fProv) return false;
      if (fDist && String(u.dist || '') !== fDist) return false;

      if (fName) {
        const full = `${u.name || ''} ${u.family || ''}`.toLowerCase();
        if (!full.includes(fName.toLowerCase())) return false;
      }

      if (fNivel && String(u.nivel || '') !== fNivel) return false;
      if (fInstit && String(u.instit || '') !== fInstit) return false;
      if (fRol && String(u.rol || '') !== fRol) return false;

      if (fGrade && gradeLabel(u.grade) !== fGrade) return false;
      if (fSection && String(u.section || '') !== fSection) return false;

      if (fAvg && !avgMatchesRangeAnyMonth(u.logros, fAvg)) return false;
      return true;
    });
    setPage(1); // reset page cuando cambian filtros
    return list;
  }, [usuariosBase, fProv, fDist, fName, fNivel, fInstit, fRol, fAvg, fGrade, fSection]);

  // ordenar por avg
  const usuariosOrdenados = useMemo(() => {
    const arr = [...usuariosFiltrados];
    if (sortAvg === 'none') return arr;

    arr.sort((a, b) => {
      const av = typeof a.avg === 'number' ? a.avg : -1;
      const bv = typeof b.avg === 'number' ? b.avg : -1;
      if (sortAvg === 'desc') return bv - av;
      return av - bv;
    });
    return arr;
  }, [usuariosFiltrados, sortAvg]);

  // paginación
  const totalPages = Math.max(1, Math.ceil(usuariosOrdenados.length / pageSize));
  const pageStart = (page - 1) * pageSize;
  const pageRows = usuariosOrdenados.slice(pageStart, pageStart + pageSize);

  // active filters list for chips
  const activeFilters = useMemo(() => {
    const acc = [];
    if (fProv) acc.push({ key: 'prov', label: `Provincia: ${fProv}` });
    if (fDist) acc.push({ key: 'dist', label: `Distrito: ${fDist}` });
    if (fName) acc.push({ key: 'name', label: `Nombre: ${fName}` });
    if (fNivel) acc.push({ key: 'nivel', label: `Nivel: ${fNivel}` });
    if (fInstit) acc.push({ key: 'instit', label: `Institución: ${fInstit}` });
    if (fRol) acc.push({ key: 'rol', label: `Rol: ${fRol}` });
    if (fGrade) acc.push({ key: 'grade', label: `Grado: ${fGrade}` });
    if (fSection) acc.push({ key: 'section', label: `Sección: ${fSection}` });
    if (fAvg) acc.push({ key: 'avg', label: `Rango esferas: ${fAvg}` });
    return acc;
  }, [fProv, fDist, fName, fNivel, fInstit, fRol, fAvg, fGrade, fSection]);

  // helpers UI
  function clearFilter(key) {
    switch (key) {
      case 'prov': setFProv(prov || ''); break;
      case 'dist': setFDist(''); break;
      case 'name': setFName(''); break;
      case 'nivel': setFNivel(''); break;
      case 'instit': setFInstit(''); break;
      case 'rol': setFRol(''); break;
      case 'avg': setFAvg(''); break;
      case 'grade': setFGrade(''); break;
      case 'section': setFSection(''); break;
      default: break;
    }
  }

  function clearAll() {
    setFProv(prov || '');
    setFDist('');
    setFName('');
    setFNivel('');
    setFInstit('');
    setFRol('');
    setFAvg('');
    setFGrade('');
    setFSection('');
  }

  function toggleExpand(id) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleExpandAll() {
    if (allExpanded) {
      setExpanded({});
      setAllExpanded(false);
    } else {
      const map = {};
      usuariosFiltrados.forEach(u => { map[u.id] = true; });
      setExpanded(map);
      setAllExpanded(true);
    }
  }

  function cycleSortAvg() {
    setSortAvg(prev => {
      if (prev === 'none') return 'desc';
      if (prev === 'desc') return 'asc';
      return 'none';
    });
  }

  // ---------- SphereAchievement + renderSpheresForUser ----------
  function MedalIcon({ size = 14 }) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        className="inline-block"
      >
        <path fill="#FBBF24" d="M12 2l2.5 4.5L19 8l-3.5 3 1 5L12 14l-4.5 2L9.5 11 6 8l4.5-1.5z" />
        <circle cx="12" cy="12" r="10" fill="none" stroke="#FBBF24" strokeWidth="0.5" />
      </svg>
    );
  }

  function SphereAchievement({ monthLabel, mr, prevMr }) {
    const pct = pctNumber(mr);
    const color = mrColor(mr);
    const label = mr != null ? `${monthLabel} — ${pct}%` : `${monthLabel} — sin data`;
    const delta = (mr != null && prevMr != null)
      ? Math.round((Number(mr) - Number(prevMr)) * 100)
      : null;

    const size = 42;
    const stroke = 4;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const dash = mr == null ? 0 : (circumference * (pct / 100));

    return (
      <div className="flex flex-col items-center gap-1 w-16">
        <div role="img" aria-label={label} title={label} className="relative">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke="#E5E7EB"
              strokeWidth={stroke}
              fill="none"
            />
            {mr == null ? (
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke="#9CA3AF"
                strokeWidth={1.5}
                fill="none"
                strokeDasharray="3 4"
              />
            ) : (
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke={color}
                strokeWidth={stroke}
                fill="none"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{
                  transition: 'stroke-dasharray 700ms cubic-bezier(.2,.9,.2,1), stroke 400ms',
                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.06))',
                }}
              />
            )}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius - stroke - 1}
              fill={mr == null ? '#F8FAFC' : '#ffffff'}
            />
          </svg>

          {mr != null ? (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-xs font-semibold" style={{ color }}>
                {pct}%
              </div>
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-xs text-gray-400">—</div>
            </div>
          )}

          {mr != null && pct > 90 && (
            <div className="absolute -right-1 -top-1">
              <span className="block" aria-hidden="true">
                <MedalIcon size={16} />
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 text-[11px] text-gray-600">
          <div className="truncate text-center w-14">{monthLabel.split(' ')[0]}</div>
          {delta != null && delta !== 0 ? (
            <div
              className={`text-[10px] font-semibold ${
                delta > 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {delta > 0 ? `▲${Math.abs(delta)}` : `▼${Math.abs(delta)}`}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  function renderSpheresForUser(u) {
    const start = startForProv(u.prov);
    const months = monthsRange(start.y, start.m);

    const mrMap = {};
    if (u.logros) {
      Object.entries(u.logros).forEach(([k, v]) => {
        if (v && typeof v.mr !== 'undefined') mrMap[k] = Number(v.mr);
      });
    }

    function prevMonthKey(key) {
      const [yStr, mStr] = key.split('_');
      let y = parseInt(yStr, 10);
      let m = parseInt(mStr, 10);
      m = m - 1;
      if (m < 1) { m = 12; y = y - 1; }
      return `${y}_${String(m).padStart(2, '0')}`;
    }

    return (
      <div className="flex gap-2 items-center overflow-x-auto snap-x snap-mandatory py-1">
        {months.map(m => {
          const logro = u.logros && u.logros[m.key] ? u.logros[m.key] : null;
          const mr = logro ? logro.mr : null;
          const prev = mrMap[prevMonthKey(m.key)] ?? null;
          return (
            <div key={m.key} className="snap-start">
              <SphereAchievement monthLabel={m.label} mr={mr} prevMr={prev} />
            </div>
          );
        })}
        {u.feedback_estudiante && (
          <i className="ml-2 text-[11px] text-gray-500">
            {u.feedback_estudiante}
          </i>
        )}
      </div>
    );
  }

  // skeleton
  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-10 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      {/* Cabecera elegante */}
      <div className="mb-4">
  <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
    
    {/* Título + descripción */}
    <div className="flex items-start gap-3">
      {/* Icono decorativo solo en pantallas medianas+ */}
      <div className="hidden sm:flex mt-0.5">
        <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
          <span className="text-indigo-500 text-lg">📚</span>
        </div>
      </div>

      <div>
        <h2 className="text-base sm:text-lg font-semibold text-slate-800">
          Tabla de Progreso Lector 2025
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Logros mensuales por estudiante. Ajusta provincia, nivel e institución para afinar la vista.
        </p>
      </div>
    </div>

    {/* Stats + orden */}
    <div className="flex flex-wrap items-center gap-2 justify-end">
      <div className="flex flex-wrap gap-1 text-[11px] text-slate-600">
        <span className="px-2 py-1 rounded-full bg-slate-50 border border-slate-200">
          Total: <strong>{usuariosBase.length}</strong>
        </span>
        <span className="px-2 py-1 rounded-full bg-indigo-50 border border-indigo-100">
          Filtrados: <strong>{usuariosFiltrados.length}</strong>
        </span>
      </div>

      <button
        onClick={cycleSortAvg}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 text-[11px] text-slate-700 bg-slate-50 hover:bg-slate-100"
      >
        Ordenar por acumulado
        {sortAvg === 'none' ? (
          <span className="text-slate-400">↕︎</span>
        ) : sortAvg === 'desc' ? (
          <span className="text-slate-700">↓</span>
        ) : (
          <span className="text-slate-700">↑</span>
        )}
      </button>
    </div>
  </div>
</div>


      {/* header filters */}
      <div className="bg-white p-3 rounded shadow-sm mb-3">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
            {/* Provincia */}
            <div>
              <label className="text-xs text-gray-600">Provincia</label>
              <select
                value={fProv}
                onChange={(e) => setFProv(e.target.value)}
                disabled={!!prov || opcionesD.prov?.length <= 1}
                className={`mt-1 w-full p-2 rounded ${
                  fProv ? 'border-2 border-blue-500' : 'border'
                } ${!!prov || (opcionesD.prov?.length <= 1) ? 'opacity-70 cursor-not-allowed' : ''}`}
                title={
                  prov
                    ? 'Bloqueado por parámetro de componente'
                    : opcionesD.prov?.length <= 1
                    ? 'Solo 1 opción disponible. Limpia filtros para cambiar.'
                    : ''
                }
              >
                <option value="">(Todas)</option>
                {opcionesD.prov?.map(o => (
                  <option key={o.val} value={o.val}>
                    {o.val} ({o.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Distrito */}
            <div>
              <label className="text-xs text-gray-600">Distrito</label>
              <select
                value={fDist}
                onChange={(e) => setFDist(e.target.value)}
                disabled={opcionesD.dist?.length <= 1}
                className={`mt-1 w-full p-2 rounded ${
                  fDist ? 'border-2 border-blue-500' : 'border'
                } ${opcionesD.dist?.length <= 1 ? 'opacity-70 cursor-not-allowed' : ''}`}
                title={
                  opcionesD.dist?.length <= 1
                    ? 'Solo 1 opción disponible. Limpia filtros para cambiar.'
                    : ''
                }
              >
                <option value="">(Todos)</option>
                {opcionesD.dist?.map(o => (
                  <option key={o.val} value={o.val}>
                    {o.val} ({o.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Nombre busqueda */}
            <div>
              <label className="text-xs text-gray-600">Nombre / Apellidos</label>
              <input
                value={fName}
                onChange={(e) => setFName(e.target.value)}
                placeholder="buscar por nombre o apellido"
                className={`mt-1 w-full p-2 rounded border ${
                  fName ? 'border-2 border-blue-500' : ''
                }`}
              />
            </div>

            {/* Nivel */}
            <div>
              <label className="text-xs text-gray-600">Nivel</label>
              <select
                value={fNivel}
                onChange={(e) => setFNivel(e.target.value)}
                disabled={opcionesD.nivel?.length <= 1}
                className={`mt-1 w-full p-2 rounded ${
                  fNivel ? 'border-2 border-blue-500' : 'border'
                } ${opcionesD.nivel?.length <= 1 ? 'opacity-70 cursor-not-allowed' : ''}`}
                title={
                  opcionesD.nivel?.length <= 1
                    ? 'Solo 1 opción disponible. Limpia filtros para cambiar.'
                    : ''
                }
              >
                <option value="">(Todos)</option>
                {opcionesD.nivel?.map(o => (
                  <option key={o.val} value={o.val}>
                    {o.val} ({o.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Institucion */}
            <div>
              <label className="text-xs text-gray-600">Institución</label>
              <select
                value={fInstit}
                onChange={(e) => setFInstit(e.target.value)}
                disabled={opcionesD.instit?.length <= 1}
                className={`mt-1 w-full p-2 rounded ${
                  fInstit ? 'border-2 border-blue-500' : 'border'
                } ${opcionesD.instit?.length <= 1 ? 'opacity-70 cursor-not-allowed' : ''}`}
                title={
                  opcionesD.instit?.length <= 1
                    ? 'Solo 1 opción disponible. Limpia filtros para cambiar.'
                    : ''
                }
              >
                <option value="">(Todos)</option>
                {opcionesD.instit?.map(o => (
                  <option key={o.val} value={o.val}>
                    {o.val} ({o.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Rol */}
            <div>
              <label className="text-xs text-gray-600">Rol</label>
              <select
                value={fRol}
                onChange={(e) => setFRol(e.target.value)}
                disabled={opcionesD.rol?.length <= 1}
                className={`mt-1 w-full p-2 rounded ${
                  fRol ? 'border-2 border-blue-500' : 'border'
                } ${opcionesD.rol?.length <= 1 ? 'opacity-70 cursor-not-allowed' : ''}`}
                title={
                  opcionesD.rol?.length <= 1
                    ? 'Solo 1 opción disponible. Limpia filtros para cambiar.'
                    : ''
                }
              >
                <option value="">(Todos)</option>
                {opcionesD.rol?.map(o => (
                  <option key={o.val} value={o.val}>
                    {o.val} ({o.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Grado */}
            <div>
              <label className="text-xs text-gray-600">Grado</label>
              <select
                value={fGrade}
                onChange={(e) => setFGrade(e.target.value)}
                disabled={opcionesD.grade?.length <= 1}
                className={`mt-1 w-full p-2 rounded ${
                  fGrade ? 'border-2 border-blue-500' : 'border'
                } ${opcionesD.grade?.length <= 1 ? 'opacity-70 cursor-not-allowed' : ''}`}
                title={
                  opcionesD.grade?.length <= 1
                    ? 'Solo 1 opción disponible. Limpia filtros para cambiar.'
                    : ''
                }
              >
                <option value="">(Todos)</option>
                {opcionesD.grade?.map(o => (
                  <option key={o.val} value={o.val}>
                    {o.val} ({o.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Sección */}
            <div>
              <label className="text-xs text-gray-600">Sección</label>
              <select
                value={fSection}
                onChange={(e) => setFSection(e.target.value)}
                disabled={opcionesD.section?.length <= 1}
                className={`mt-1 w-full p-2 rounded ${
                  fSection ? 'border-2 border-blue-500' : 'border'
                } ${opcionesD.section?.length <= 1 ? 'opacity-70 cursor-not-allowed' : ''}`}
                title={
                  opcionesD.section?.length <= 1
                    ? 'Solo 1 opción disponible. Limpia filtros para cambiar.'
                    : ''
                }
              >
                <option value="">(Todas)</option>
                {opcionesD.section?.map(o => (
                  <option key={o.val} value={o.val}>
                    {o.val} ({o.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Avg (rango esferas) */}
            <div>
              <label className="text-xs text-gray-600">Esferas (rango)</label>
              <select
                value={fAvg}
                onChange={(e) => setFAvg(e.target.value)}
                className={`mt-1 w-full p-2 rounded ${
                  fAvg ? 'border-2 border-blue-500' : 'border'
                }`}
              >
                <option value="">🌟 Todos los logros</option>
                <option value="0-45">🔴 Explorador@s Iniciales 🧭(≤ 45%)</option>
                <option value="46-65">🟠 Aventurer@s de Páginas 🎒(46–65%)</option>
                <option value="66-80">🟡 Centinelas del Saber 🛡️(66–80%)</option>
                <option value="81-90">🔵 Lectores Guardianes de Historias ✒️(81–90%)</option>
                <option value="91-100">🟢 Leyendas Lectoras ⭐(90%-)</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <button
                onClick={clearAll}
                className="px-3 py-2 bg-gray-100 rounded text-sm"
              >
                Limpiar
              </button>
              <button
                onClick={toggleExpandAll}
                className="px-3 py-2 bg-indigo-50 text-indigo-700 rounded text-sm"
              >
                {allExpanded ? 'Ocultar todos' : 'Mostrar detalles todos'}
              </button>
            </div>
            <div className="text-sm text-gray-600">
              Mostrando <strong>{usuariosFiltrados.length}</strong> de{' '}
              <strong>{usuariosBase.length}</strong>
            </div>
          </div>
        </div>

        {/* chips */}
        <div className="mt-3">
          {activeFilters.length > 0 ? (
            <div className="flex gap-2 flex-wrap">
              {activeFilters.map(f => (
                <button
                  key={f.key}
                  onClick={() => clearFilter(f.key)}
                  className="inline-flex items-center gap-2 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs"
                >
                  {f.label}{' '}
                  <span className="ml-1 text-blue-400">×</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-500">Sin filtros activos</div>
          )}
        </div>
      </div>

      {/* tabla */}
      <div className="bg-white rounded shadow-sm overflow-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="p-2 text-left">Provincia</th>
              <th className="p-2 text-left">Distrito</th>
              <th className="p-2 text-left">Nombre completo</th>
              <th className="p-2 text-left">Nivel</th>
              <th className="p-2 text-left">Institución</th>
              <th className="p-2 text-left">Rol</th>
              <th className="p-2 text-left">Grado</th>
              <th className="p-2 text-left">Sección</th>
              <th className="p-2 text-left">
                <button
                  onClick={cycleSortAvg}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700"
                >
                  Acumulado
                  {sortAvg === 'none' ? '↕︎' : sortAvg === 'desc' ? '↓' : '↑'}
                </button>
              </th>
              <th className="p-2 text-left">Meses</th>
              <th className="p-2 text-left">Acción</th>
            </tr>
          </thead>

          <tbody className="transition-all duration-200 ease-in-out">
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={11} className="p-6 text-center text-gray-500">
                  No hay usuarios que coincidan con los filtros. Intenta limpiar filtros.
                </td>
              </tr>
            ) : null}

            {pageRows.map(u => (
              <React.Fragment key={u.id}>
                <tr
                  className={
                    'border-t transition-colors ' +
                    (expanded[u.id]
                      ? 'bg-indigo-50 border-2 border-indigo-300'
                      : 'hover:bg-gray-50')
                  }
                >
                  <td className="p-2 align-top w-28 text-sm">{u.prov || '-'}</td>
                  <td className="p-2 align-top w-28 text-sm">{u.dist || '-'}</td>
                  <td className="p-2 align-top">
                    <div className="font-medium">
                      {u.name || '-'} {u.family || ''}
                    </div>
                  </td>
                  <td className="p-2 align-top text-sm">{u.nivel || '-'}</td>
                  <td className="p-2 align-top text-sm">{u.instit || '-'}</td>
                  <td className="p-2 align-top text-sm">{u.rol || '-'}</td>
                  <td className="p-2 align-top text-sm">{gradeLabel(u.grade)}</td>
                  <td className="p-2 align-top text-sm">{u.section || '-'}</td>
                  <td className="p-2 align-top text-sm">
                    {typeof u.avg !== 'undefined' && u.avg != null
                      ? `${(Number(u.avg) * 100).toFixed(1)}%`
                      : '-'}
                  </td>
                  <td className="p-2 align-top">
                    <div className="hidden sm:flex gap-2 items-center">
                      {renderSpheresForUser(u)}
                    </div>
                    {/* mobile: show summary */}
                    <div className="sm:hidden text-xs text-gray-600">
                      Tap "Detalles" para meses
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleExpand(u.id)}
                        className="px-2 py-1 bg-gray-100 rounded text-sm"
                      >
                        {expanded[u.id] ? 'Ocultar' : 'Detalles'}
                      </button>
                     
                    </div>
                  </td>
                </tr>

                {/* mobile expanded area */}
                {expanded[u.id] ? (
                  <tr className="sm:hidden bg-gray-50">
                    <td colSpan={11} className="p-3">
                      <div className="mb-2 text-sm font-medium">Meses</div>
                      <div className="flex gap-2 flex-wrap">
                        {renderSpheresForUser(u)}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* paginación simple */}
      <div className="mt-3 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Página {page} / {totalPages} — {usuariosOrdenados.length} resultados
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            className="px-2 py-1 rounded bg-gray-100 disabled:opacity-50"
          >
            Primera
          </button>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-2 py-1 rounded bg-gray-100 disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-2 py-1 rounded bg-gray-100 disabled:opacity-50"
          >
            Siguiente
          </button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            className="px-2 py-1 rounded bg-gray-100 disabled:opacity-50"
          >
            Última
          </button>
        </div>
      </div>

      {/* leyenda fija */}
      <div className="mt-4 text-xs text-gray-600">
        <strong>Leyenda:</strong>
        <div className="flex gap-3 mt-2 items-center flex-wrap">
          <Leyenda color="#9CA3AF" label="Sin data (ploma)" />
          <Leyenda color="#DC2626" label="≤ 45% (rojo)" />
          <Leyenda color="#F97316" label="46 - 65% (naranja)" />
          <Leyenda color="#F59E0B" label="66 - 80% (amarillo)" />
          <Leyenda color="#3B82F6" label="81 - 90% (azul)" />
          <Leyenda color="#16A34A" label="> 90% (verde)" />
        </div>
      </div>
    </div>
  );
}

function Leyenda({ color, label }) {
  return (
    <div className="flex items-center gap-2">
      <div
        style={{ backgroundColor: color }}
        className="w-4 h-4 rounded-full ring-1 ring-white"
      />
      <div className="text-xs">{label}</div>
    </div>
  );
}
